import * as React from 'react';
import { Card, CardContent, Modal, Select, MenuItem, Button } from '@material-ui/core';
import {
  Show,
  SimpleShowLayout,
  TextField,
  FunctionField,
  DeleteButton,
  usePermissions
} from 'react-admin';

import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import ImgField from '../components/ImgField';
import { PageHeader, Surface, DetailGrid, DetailItem, StatusPill, ActionButton, Avatar } from '../components/ui';

// ── Shared constants ──────────────────────────────────────────────
const CATEGORY_CHOICES = [
  { id: 'home_maintenance', name: 'Home & Maintenance' },
  { id: 'cleaning_domestic', name: 'Cleaning & Domestic Services' },
  { id: 'automotive_transport', name: 'Automotive & Transport' },
  { id: 'delivery_logistics', name: 'Delivery & Logistics' },
  { id: 'digital_tech', name: 'Digital & Tech' },
  { id: 'education_training', name: 'Education & Training' },
  { id: 'health_wellness', name: 'Health, Wellness & Personal Care' },
  { id: 'food_events', name: 'Food & Events' },
  { id: 'art_media', name: 'Art & Media' },
  { id: 'business_professional', name: 'Business & Professional Services' },
];

const STATUS_CHOICES = [
  { id: 'PUBLISHED', name: 'Published' },
  { id: 'DRAFT', name: 'Draft' },
];

const FIELD_TYPES = ['TEXT','NUMBER','BOOLEAN','LOCATION','IMAGE','DATE','DATETIME','SELECT','MULTISELECT'];

const newEmptyField = () => ({ fieldName: '', fieldType: 'TEXT', options: '', description: '', isRequired: false, showIf: null });

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getPriorFieldOptions = (f) => {
  if (!f) return [];
  if ((f.fieldType === 'SELECT' || f.fieldType === 'MULTISELECT') && f.options) {
    return f.options.split(',').map(o => o.trim()).filter(Boolean);
  }
  return [];
};

const processFormFieldsForApi = (formFields) =>
  formFields.map(field => {
    const base = {
      fieldName: field.fieldName,
      description: field.description || '',
      isRequired: field.isRequired || false,
      ...(field.showIf && field.showIf.field && field.showIf.containsAny && field.showIf.containsAny.length > 0
        ? { showIf: { field: field.showIf.field, containsAny: field.showIf.containsAny } }
        : {})
    };
    if (field.fieldType === 'SELECT' && field.options) return { ...base, fieldType: `SELECT:${field.options}` };
    if (field.fieldType === 'MULTISELECT' && field.options) return { ...base, fieldType: `MULTISELECT:${field.options}` };
    if (field.fieldType === 'LOCATION') return { ...base, fieldType: 'LOCATION', locationDetail: field.locationDetail || 'full' };
    return { ...base, fieldType: field.fieldType };
  });

// ── SVG icons ─────────────────────────────────────────────────────
const PlusIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const CondIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 12h2m4 0h2m4 0h2M7 8l-3 4 3 4M17 8l3 4-3 4" />
  </svg>
);

// ── Shared form field card renderer ──────────────────────────────
const FieldCard = ({ field, index, formFields, updateFormField, removeFormField, insertFormField }) => {
  const isConditional = !!(field.showIf && field.showIf.field);
  const priorSelectFields = index > 0
    ? formFields.slice(0, index).filter(f => (f.fieldType === 'SELECT' || f.fieldType === 'MULTISELECT') && f.fieldName && f.options)
    : [];

  return (
    <React.Fragment>
      <div className="sk-svc-field-card">
        <div className="sk-svc-field-header">
          <span className="sk-svc-field-num">{index + 1}</span>
          <button type="button" className="sk-svc-btn-remove" onClick={() => removeFormField(index)}>✕ Remove</button>
        </div>

        <div className="sk-svc-field-grid">
          <div className="sk-svc-form-group">
            <label>Field Name</label>
            <input type="text" value={field.fieldName} onChange={(e) => updateFormField(index, 'fieldName', e.target.value)} placeholder="e.g., From, To, Package size" className="sk-control" />
          </div>
          <div className="sk-svc-form-group">
            <label>Type</label>
            <select value={field.fieldType} onChange={(e) => updateFormField(index, 'fieldType', e.target.value)} className="sk-control">
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="sk-svc-form-group">
            <label>Description</label>
            <input type="text" value={field.description} onChange={(e) => updateFormField(index, 'description', e.target.value)} placeholder="Brief description…" className="sk-control" />
          </div>
        </div>

        {(field.fieldType === 'SELECT' || field.fieldType === 'MULTISELECT') && (
          <div className="sk-svc-form-group" style={{ marginTop: 10 }}>
            <label>Options (comma-separated)</label>
            <input type="text" value={field.options} onChange={(e) => updateFormField(index, 'options', e.target.value)} placeholder="e.g., small,medium,large" className="sk-control" />
          </div>
        )}

        {field.fieldType === 'LOCATION' && (
          <div className="sk-svc-form-group" style={{ marginTop: 10 }}>
            <label>Location detail</label>
            <select value={field.locationDetail || 'full'} onChange={(e) => updateFormField(index, 'locationDetail', e.target.value)} className="sk-control">
              <option value="full">Full</option>
              <option value="coarse">Pin + landmark</option>
            </select>
          </div>
        )}

        <div className="sk-svc-toggle-row">
          <label className="sk-svc-toggle-label">
            <input type="checkbox" checked={field.isRequired || false} onChange={(e) => updateFormField(index, 'isRequired', e.target.checked)} />
            Required
          </label>
          {priorSelectFields.length > 0 && (
            <label className="sk-svc-toggle-label">
              <input
                type="checkbox"
                checked={isConditional}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFormField(index, 'showIf', { field: priorSelectFields[0].fieldName, containsAny: [] });
                  } else {
                    updateFormField(index, 'showIf', null);
                  }
                }}
              />
              Show conditionally
            </label>
          )}
        </div>

        {isConditional && (() => {
          const triggerField = formFields.find(f => f.fieldName === field.showIf.field);
          const opts = getPriorFieldOptions(triggerField);
          return (
            <div className="sk-svc-showif">
              <div className="sk-svc-showif-title"><CondIcon /> Conditional Visibility</div>
              <div className="sk-svc-form-group" style={{ marginBottom: 10 }}>
                <div className="sk-svc-showif-trigger">Show this field when answer to:</div>
                <select
                  value={field.showIf.field || ''}
                  onChange={(e) => updateFormField(index, 'showIf', { field: e.target.value, containsAny: [] })}
                  className="sk-control" style={{ fontSize: 12 }}
                >
                  {priorSelectFields.map(pf => <option key={pf.fieldName} value={pf.fieldName}>{pf.fieldName}</option>)}
                </select>
              </div>
              <div>
                <div className="sk-svc-showif-trigger" style={{ marginBottom: 6 }}>Contains any of:</div>
                <div className="sk-svc-option-chips">
                  {opts.map(opt => {
                    const sel = (field.showIf.containsAny || []).includes(opt);
                    return (
                      <label key={opt} className={`sk-svc-chip${sel ? ' selected' : ''}`}>
                        <input
                          type="checkbox" checked={sel}
                          onChange={(e) => {
                            const cur = field.showIf.containsAny || [];
                            const upd = e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt);
                            updateFormField(index, 'showIf', { ...field.showIf, containsAny: upd });
                          }}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="sk-svc-insert">
        <button type="button" className="sk-svc-insert-btn" onClick={() => insertFormField(index)}>
          <PlusIcon size={12} /> Insert field here
        </button>
      </div>
    </React.Fragment>
  );
};


// ══════════════════════════════════════════════════════════════════
// CREATE
// ══════════════════════════════════════════════════════════════════
export const DefaultServicesCreate = (props) => {
  const [formData, setFormData] = React.useState({ name: '', defaultDesc: '', tags: '', category: '' });
  const [formFields, setFormFields] = React.useState([]);
  const [registrationFields, setRegistrationFields] = React.useState([]);
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [activeFlowTab, setActiveFlowTab] = React.useState('request');
  const [flowTemplates, setFlowTemplates] = React.useState([]);
  const [requestTemplateId, setRequestTemplateId] = React.useState('');
  const [registrationTemplateId, setRegistrationTemplateId] = React.useState('');

  // Fetch flow templates on mount
  React.useEffect(() => {
    httpClient(`${API_BASE_URL}/flowTemplates`)
      .then(({ json }) => { console.log('FlowTemplates loaded:', json?.length); setFlowTemplates(Array.isArray(json) ? json : []); })
      .catch(err => console.error('FlowTemplates fetch failed:', err));
  }, []);

  const applyTemplate = (templateId, flowType) => {
    const tmpl = flowTemplates.find(t => t._id === templateId);
    if (!tmpl) return;
    const parsedFields = (tmpl.fields || []).map(f => ({
      fieldName: f.fieldName || '',
      fieldType: f.fieldType || 'TEXT',
      description: f.description || '',
      isRequired: f.isRequired || false,
      showIf: f.showIf || { field: '', containsAny: [] }
    }));
    if (flowType === 'request') {
      setFormFields(parsedFields);
    } else {
      setRegistrationFields(parsedFields);
    }
  }; 

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addFormField = () => setFormFields([...formFields, newEmptyField()]);
  const removeFormField = (index) => setFormFields(formFields.filter((_, i) => i !== index));
  const insertFormField = (afterIndex) => {
    const updated = [...formFields];
    updated.splice(afterIndex + 1, 0, newEmptyField());
    setFormFields(updated);
  };
  const updateFormField = (index, field, value) => {
    const updated = [...formFields];
    updated[index][field] = value;
    setFormFields(updated);
  };

  const addRegistrationField = () => setRegistrationFields([...registrationFields, newEmptyField()]);
  const removeRegistrationField = (index) => setRegistrationFields(registrationFields.filter((_, i) => i !== index));
  const insertRegistrationField = (afterIndex) => {
    const updated = [...registrationFields];
    updated.splice(afterIndex + 1, 0, newEmptyField());
    setRegistrationFields(updated);
  };
  const updateRegistrationField = (index, field, value) => {
    const updated = [...registrationFields];
    updated[index][field] = value;
    setRegistrationFields(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let b64ImageData = null;
      if (imageFile) b64ImageData = await convertFileToBase64(imageFile);
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        name: formData.name,
        defaultDesc: formData.defaultDesc,
        tags: tagsArray,
        category: formData.category,
        formFields: processFormFieldsForApi(formFields),
        registrationFields: processFormFieldsForApi(registrationFields),
        requestTemplateId: requestTemplateId || null,
        registrationTemplateId: registrationTemplateId || null,
        ...(b64ImageData && { b64ImageData })
      };
      await httpClient('https://skilzapp.net/api/defaultservices/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      window.location.hash = '#/defaultservices';
    } catch (err) {
      setError(err.message || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="sk-page sk-svc-form">
      <PageHeader eyebrow="Catalogue" title="Create New Default Service" />
      <form onSubmit={handleSubmit}>
        {/* ── Card 1: Service Details ── */}
        <div className="sk-svc-surface">
          <div className="sk-svc-section-header">
            <h3>Service Details</h3>
          </div>
          <div className="sk-svc-form-group">
            <label>Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="sk-control" placeholder="e.g., Private Tutor (مُدرّس(ة) خصوصي)" />
          </div>
          <div className="sk-svc-form-group">
            <label>Description</label>
            <textarea name="defaultDesc" value={formData.defaultDesc} onChange={handleInputChange} required rows={3} className="sk-control" placeholder="Brief service description…" />
          </div>
          <div className="sk-svc-form-row">
            <div className="sk-svc-form-group">
              <label>Category</label>
              <select name="category" value={formData.category} onChange={handleInputChange} required className="sk-control">
                <option value="">Select a category…</option>
                {CATEGORY_CHOICES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="sk-svc-form-group">
            <label>Tags (comma-separated)</label>
            <input type="text" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="e.g., plumbing, repair, maintenance" className="sk-control" />
          </div>
          <div className="sk-svc-form-group">
            <label>Featured Image</label>
            <div className="sk-svc-img-wrap">
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input type="file" accept="image/*" onChange={handleImageChange} className="sk-control" />
                {imagePreview && <img src={imagePreview} alt="Preview" className="sk-svc-img-preview" />}
                <span style={{ fontSize: 11, color: '#7f8999' }}>Upload a featured image</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs: Send Request Flow / Registration Flow ── */}
        <nav className="sk-ds__tabs" role="tablist" aria-label="Configuration flow" style={{ marginBottom: 0 }}>
          <button role="tab" type="button" aria-selected={activeFlowTab === 'request'} className={`sk-ds__tab ${activeFlowTab === 'request' ? 'active' : ''} tone-green`} onClick={() => setActiveFlowTab('request')}>
            <span className="sk-ds__tab-label">Send Request Flow</span>
            <span className="sk-ds__tab-count">{formFields.length}</span>
          </button>
          <button role="tab" type="button" aria-selected={activeFlowTab === 'registration'} className={`sk-ds__tab ${activeFlowTab === 'registration' ? 'active' : ''} tone-green`} onClick={() => setActiveFlowTab('registration')}>
            <span className="sk-ds__tab-label">Registration Flow</span>
            <span className="sk-ds__tab-count">{registrationFields.length}</span>
          </button>
        </nav>

        {activeFlowTab === 'request' && (
        <div className="sk-svc-card">
          <div className="sk-svc-card-header">
            <div><h3>Send Request Fields <span className="sk-svc-badge">{formFields.length}</span></h3></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="sk-control" style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, borderColor: '#7c3aed', minWidth: 180 }} value={requestTemplateId} onChange={e => { const v = e.target.value; setRequestTemplateId(v); if (v) applyTemplate(v, 'request'); }}>
                <option value="">— No template (custom) —</option>
                {flowTemplates.filter(t => t.type === 'request').map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
              <button type="button" className="sk-svc-add-btn" onClick={addFormField}><PlusIcon /> Add Field</button>
            </div>
          </div>
          {formFields.length === 0 ? (
            <p className="sk-svc-empty">No custom fields added yet.</p>
          ) : (
            formFields.map((field, index) => (
              <FieldCard
                key={index} field={field} index={index} formFields={formFields}
                updateFormField={updateFormField} removeFormField={removeFormField} insertFormField={insertFormField}
              />
            ))
          )}
        </div>
        )}

        {activeFlowTab === 'registration' && (
        <div className="sk-svc-card">
          <div className="sk-svc-card-header">
            <div><h3>Registration Fields <span className="sk-svc-badge">{registrationFields.length}</span></h3></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="sk-control" style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, borderColor: '#7c3aed', minWidth: 180 }} value={registrationTemplateId} onChange={e => { const v = e.target.value; setRegistrationTemplateId(v); if (v) applyTemplate(v, 'registration'); }}>
                <option value="">— No template (custom) —</option>
                {flowTemplates.filter(t => t.type === 'registration').map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
              <button type="button" className="sk-svc-add-btn" onClick={addRegistrationField}><PlusIcon /> Add Field</button>
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#7f8999', margin: '0 0 14px' }}>
            Custom questions asked during provider registration for this service.
          </p>
          {registrationFields.length === 0 ? (
            <p className="sk-svc-empty">No registration fields added yet.</p>
          ) : (
            registrationFields.map((field, index) => (
              <FieldCard
                key={index} field={field} index={index} formFields={registrationFields}
                updateFormField={updateRegistrationField} removeFormField={removeRegistrationField} insertFormField={insertRegistrationField}
              />
            ))
          )}
        </div>
        )}

        {error && <div style={{ color: '#ef4444', padding: '10px 14px', marginBottom: 16, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13 }}>Error: {error}</div>}
        <div className="sk-svc-actions">
          <ActionButton type="submit" disabled={loading}>{loading ? 'Creating…' : 'Create'}</ActionButton>
          <ActionButton variant="secondary" type="button" onClick={() => window.location.hash = '#/defaultservices'}>Cancel</ActionButton>
        </div>
      </form>
    </main>
  );
};


const CREDENTIAL_TYPE_OPTIONS = [
  { id: 'syndicate_card', name: 'Syndicate Card' },
  { id: 'university_degree', name: 'University Degree' },
  { id: 'professional_license', name: 'Professional License' },
  { id: 'company_registration', name: 'Company Registration' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'other', name: 'Other' },
];

const DEFAULT_SERVICE_MODES = [
  { key: 'hasStore', defaultLabel: { en: 'Store', ar: 'محل' }, defaultDesc: { en: '', ar: '' } },
  { key: 'homeService', defaultLabel: { en: 'Home Service', ar: 'خدمة منزلية' }, defaultDesc: { en: '', ar: '' } },
  { key: 'delivery', defaultLabel: { en: 'Delivery', ar: 'توصيل' }, defaultDesc: { en: '', ar: '' } },
];

// ══════════════════════════════════════════════════════════════════
// EDIT
// ══════════════════════════════════════════════════════════════════
export const DefaultServicesEdit = (props) => {
  const { permissions } = usePermissions();
  const isSuperAdmin = permissions && permissions.adminRoles && permissions.adminRoles.includes('SUPER_ADMIN');
  const [formData, setFormData] = React.useState({ name: '', defaultDesc: '', tags: '', status: '', category: '' });
  const [formFields, setFormFields] = React.useState([]);
  const [registrationFields, setRegistrationFields] = React.useState([]);
  const [currentImage, setCurrentImage] = React.useState(null);
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [activeFlowTab, setActiveFlowTab] = React.useState('request');
  const [flowTemplates, setFlowTemplates] = React.useState([]);
  const [requestTemplateId, setRequestTemplateId] = React.useState('');
  const [registrationTemplateId, setRegistrationTemplateId] = React.useState('');
  // Credentials config state
  const [requiresCredentials, setRequiresCredentials] = React.useState(false);
  const [credentialTypes, setCredentialTypes] = React.useState([]);
  const [registrationConfig, setRegistrationConfig] = React.useState({
    serviceModes: DEFAULT_SERVICE_MODES.map(m => ({
      key: m.key,
      label: { ...m.defaultLabel },
      description: { ...m.defaultDesc },
      enabled: false,
    })),
    storeAddressLabel: { en: 'Address', ar: 'العنوان' },
  });

  React.useEffect(() => { loadData(); }, [props.id]);
  React.useEffect(() => {
    httpClient(`${API_BASE_URL}/flowTemplates`)
      .then(({ json }) => { console.log('FlowTemplates loaded:', json?.length); setFlowTemplates(Array.isArray(json) ? json : []); })
      .catch(err => console.error('FlowTemplates fetch failed:', err));
  }, []);

  const applyTemplate = (templateId, flowType) => {
    const tmpl = flowTemplates.find(t => t._id === templateId);
    if (!tmpl) return;
    const parsedFields = (tmpl.fields || []).map(f => ({
      fieldName: f.fieldName || '',
      fieldType: f.fieldType || 'TEXT',
      description: f.description || '',
      isRequired: f.isRequired || false,
      showIf: f.showIf || { field: '', containsAny: [] }
    }));
    if (flowType === 'request') {
      setFormFields(parsedFields);
    } else {
      setRegistrationFields(parsedFields);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { json } = await httpClient('https://skilzapp.net/api/defaultservices/' + props.id);
      setFormData({
        name: json.name || '',
        defaultDesc: json.defaultDesc || '',
        tags: Array.isArray(json.tags) ? json.tags.join(', ') : '',
        status: json.status || '',
        category: json.category || ''
      });
      setCurrentImage(json.featuredImage);
      setRequestTemplateId(json.requestTemplateId || '');
      setRegistrationTemplateId(json.registrationTemplateId || '');
      // Load credentials config
      setRequiresCredentials(json.requiresCredentials || false);
      setCredentialTypes(json.credentialTypes || []);
      if (json.registrationConfig) {
        const rc = json.registrationConfig;
        setRegistrationConfig({
          serviceModes: DEFAULT_SERVICE_MODES.map(m => {
            const existing = (rc.serviceModes || []).find(s => s.key === m.key);
            return existing ? { ...existing } : {
              key: m.key,
              label: { ...m.defaultLabel },
              description: { ...m.defaultDesc },
              enabled: false,
            };
          }),
          storeAddressLabel: rc.storeAddressLabel || { en: 'Address', ar: 'العنوان' },
        });
      }
      if (json.formFields && Array.isArray(json.formFields)) {
        const parsedFields = json.formFields.map(field => {
          const base = {
            fieldName: field.fieldName,
            description: field.description || '',
            isRequired: field.isRequired || false,
            options: '',
            locationDetail: field.locationDetail || 'full',
            showIf: field.showIf || null
          };
          if (field.fieldType.startsWith('SELECT:')) return { ...base, fieldType: 'SELECT', options: field.fieldType.replace('SELECT:', '') };
          if (field.fieldType.startsWith('MULTISELECT:')) return { ...base, fieldType: 'MULTISELECT', options: field.fieldType.replace('MULTISELECT:', '') };
          return { ...base, fieldType: field.fieldType };
        });
        setFormFields(parsedFields);
      }
      // Parse registration fields
      if (json.registrationFields && Array.isArray(json.registrationFields)) {
        const parsedRegFields = json.registrationFields.map(field => {
          const base = {
            fieldName: field.fieldName,
            description: field.description || '',
            isRequired: field.isRequired || false,
            options: '',
            locationDetail: field.locationDetail || 'full',
            showIf: field.showIf || null
          };
          if (field.fieldType.startsWith('SELECT:')) return { ...base, fieldType: 'SELECT', options: field.fieldType.replace('SELECT:', '') };
          if (field.fieldType.startsWith('MULTISELECT:')) return { ...base, fieldType: 'MULTISELECT', options: field.fieldType.replace('MULTISELECT:', '') };
          return { ...base, fieldType: field.fieldType };
        });
        setRegistrationFields(parsedRegFields);
      }
    } catch (err) {
      setError('Failed to load service data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addFormField = () => setFormFields([...formFields, newEmptyField()]);
  const removeFormField = (index) => setFormFields(formFields.filter((_, i) => i !== index));
  const insertFormField = (afterIndex) => {
    const updated = [...formFields];
    updated.splice(afterIndex + 1, 0, newEmptyField());
    setFormFields(updated);
  };
  const updateFormField = (index, field, value) => {
    const updated = [...formFields];
    updated[index][field] = value;
    setFormFields(updated);
  };

  const addRegistrationField = () => setRegistrationFields([...registrationFields, newEmptyField()]);
  const removeRegistrationField = (index) => setRegistrationFields(registrationFields.filter((_, i) => i !== index));
  const insertRegistrationField = (afterIndex) => {
    const updated = [...registrationFields];
    updated.splice(afterIndex + 1, 0, newEmptyField());
    setRegistrationFields(updated);
  };
  const updateRegistrationField = (index, field, value) => {
    const updated = [...registrationFields];
    updated[index][field] = value;
    setRegistrationFields(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let b64ImageData = null;
      if (imageFile) b64ImageData = await convertFileToBase64(imageFile);
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        name: formData.name,
        defaultDesc: formData.defaultDesc,
        tags: tagsArray,
        status: formData.status,
        category: formData.category,
        formFields: processFormFieldsForApi(formFields),
        registrationFields: processFormFieldsForApi(registrationFields),
        requiresCredentials,
        credentialTypes,
        registrationConfig,
        requestTemplateId: requestTemplateId || null,
        registrationTemplateId: registrationTemplateId || null,
        ...(b64ImageData && { b64ImageData })
      };
      await httpClient('https://skilzapp.net/api/defaultservices/' + props.id, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      window.location.hash = '#/defaultservices';
    } catch (err) {
      setError(err.message || 'Failed to update service');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="sk-page sk-svc-form">
        <PageHeader eyebrow="Catalogue" title="Loading…" />
        <div className="sk-svc-surface"><p className="sk-muted">Loading…</p></div>
      </main>
    );
  }

  return (
    <main className="sk-page sk-svc-form">
      <PageHeader eyebrow="Catalogue" title="Edit Default Service" />
      <form onSubmit={handleSubmit}>
        {/* ── Card 1: Service Details ── */}
        <div className="sk-svc-surface">
          <div className="sk-svc-section-header">
            <h3>Service Details</h3>
            <StatusPill status={formData.status}>{formData.status}</StatusPill>
          </div>
          <div className="sk-svc-form-group">
            <label>Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="sk-control" />
          </div>
          <div className="sk-svc-form-group">
            <label>Description</label>
            <textarea name="defaultDesc" value={formData.defaultDesc} onChange={handleInputChange} required rows={3} className="sk-control" />
          </div>
          <div className="sk-svc-form-row">
            <div className="sk-svc-form-group">
              <label>Category</label>
              <select name="category" value={formData.category} onChange={handleInputChange} required className="sk-control">
                <option value="">Select a category…</option>
                {CATEGORY_CHOICES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="sk-svc-form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange} required className="sk-control">
                <option value="">Select status…</option>
                {STATUS_CHOICES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="sk-svc-form-group">
            <label>Tags (comma-separated)</label>
            <input type="text" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="e.g., plumbing, repair, maintenance" className="sk-control" />
          </div>
          <div className="sk-svc-form-group">
            <label>Featured Image</label>
            <div className="sk-svc-img-wrap">
              {currentImage && (
                <img src={`https://skilzcdn.nyc3.cdn.digitaloceanspaces.com/${currentImage}`} alt="Current" className="sk-svc-img-preview" />
              )}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input type="file" accept="image/*" onChange={handleImageChange} className="sk-control" />
                {imagePreview && <img src={imagePreview} alt="Preview" className="sk-svc-img-preview" />}
                <span style={{ fontSize: 11, color: '#7f8999' }}>
                  {currentImage ? 'Replace the current image by uploading a new one' : 'Upload a featured image'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs: Send Request Flow / Registration Flow ── */}
        <nav className="sk-ds__tabs" role="tablist" aria-label="Configuration flow" style={{ marginBottom: 0 }}>
          <button role="tab" type="button" aria-selected={activeFlowTab === 'request'} className={`sk-ds__tab ${activeFlowTab === 'request' ? 'active' : ''} tone-green`} onClick={() => setActiveFlowTab('request')}>
            <span className="sk-ds__tab-label">Send Request Flow</span>
            <span className="sk-ds__tab-count">{formFields.length}</span>
          </button>
          <button role="tab" type="button" aria-selected={activeFlowTab === 'registration'} className={`sk-ds__tab ${activeFlowTab === 'registration' ? 'active' : ''} tone-green`} onClick={() => setActiveFlowTab('registration')}>
            <span className="sk-ds__tab-label">Registration Flow</span>
            <span className="sk-ds__tab-count">{registrationFields.length}</span>
          </button>
        </nav>

        {activeFlowTab === 'request' && (
        <div className="sk-svc-card">
          <div className="sk-svc-card-header">
            <div><h3>Send Request Fields <span className="sk-svc-badge">{formFields.length}</span></h3></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="sk-control" style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, borderColor: '#7c3aed', minWidth: 180 }} value={requestTemplateId} onChange={e => { const v = e.target.value; setRequestTemplateId(v); if (v) applyTemplate(v, 'request'); }}>
                <option value="">— No template (custom) —</option>
                {flowTemplates.filter(t => t.type === 'request').map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
              <button type="button" className="sk-svc-add-btn" onClick={addFormField}><PlusIcon /> Add Field</button>
            </div>
          </div>
          {formFields.length === 0 ? (
            <p className="sk-svc-empty">No custom fields added yet.</p>
          ) : (
            formFields.map((field, index) => (
              <FieldCard
                key={index} field={field} index={index} formFields={formFields}
                updateFormField={updateFormField} removeFormField={removeFormField} insertFormField={insertFormField}
              />
            ))
          )}
        </div>
        )}

        {activeFlowTab === 'registration' && (
        <>
          {/* Fixed Fields: Credentials & Service Modes */}
          {isSuperAdmin && (
          <div className="sk-svc-card">
            <div className="sk-svc-card-header">
              <h3>Fixed Registration Fields</h3>
            </div>

            <div className="sk-svc-field" style={{ marginBottom: 16 }}>
              <label className="sk-svc-label">
                <input
                  type="checkbox"
                  checked={requiresCredentials}
                  onChange={e => setRequiresCredentials(e.target.checked)}
                />
                Requires Credentials
              </label>
              <span style={{ fontSize: 11, color: '#7f8999' }}>
                Providers must upload professional documents for verification
              </span>
            </div>

            {requiresCredentials && (
              <>
                <div className="sk-svc-field" style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#566071', marginBottom: 6, display: 'block' }}>
                    Required Credential Types
                  </label>
                  <div className="sk-svc-chips">
                    {CREDENTIAL_TYPE_OPTIONS.map(ct => {
                      const selected = credentialTypes.includes(ct.id);
                      return (
                        <label key={ct.id} className={`sk-svc-chip${selected ? ' selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={e => {
                              if (e.target.checked) setCredentialTypes([...credentialTypes, ct.id]);
                              else setCredentialTypes(credentialTypes.filter(t => t !== ct.id));
                            }}
                          />
                          {ct.name}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div style={{ marginTop: 18, borderTop: '1px solid var(--sk-border)', paddingTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#566071', marginBottom: 12 }}>
                    Registration Service Modes
                  </div>
                  <span style={{ fontSize: 11, color: '#7f8999', display: 'block', marginBottom: 14 }}>
                    Configure which service modes are available during registration and customize their labels per service.
                  </span>

                  {registrationConfig.serviceModes.map((mode, idx) => (
                    <div key={mode.key} style={{ marginBottom: 12, padding: 14, border: '1px solid var(--sk-border)', borderRadius: 10, background: 'var(--sk-surface)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <label className="sk-svc-label">
                          <input type="checkbox" checked={mode.enabled} onChange={e => {
                            const updated = [...registrationConfig.serviceModes];
                            updated[idx] = { ...updated[idx], enabled: e.target.checked };
                            setRegistrationConfig({ ...registrationConfig, serviceModes: updated });
                          }} />
                          <strong>{mode.key}</strong>
                        </label>
                      </div>
                      {mode.enabled && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div className="sk-svc-field"><label>Label (EN)</label>
                            <input type="text" value={mode.label?.en || ''} onChange={e => { const u = [...registrationConfig.serviceModes]; u[idx] = { ...u[idx], label: { ...u[idx].label, en: e.target.value } }; setRegistrationConfig({ ...registrationConfig, serviceModes: u }); }} className="sk-control" placeholder="e.g., Clinic" />
                          </div>
                          <div className="sk-svc-field"><label>Label (AR)</label>
                            <input type="text" value={mode.label?.ar || ''} onChange={e => { const u = [...registrationConfig.serviceModes]; u[idx] = { ...u[idx], label: { ...u[idx].label, ar: e.target.value } }; setRegistrationConfig({ ...registrationConfig, serviceModes: u }); }} className="sk-control" placeholder="e.g., عيادة" dir="rtl" />
                          </div>
                          <div className="sk-svc-field"><label>Description (EN)</label>
                            <input type="text" value={mode.description?.en || ''} onChange={e => { const u = [...registrationConfig.serviceModes]; u[idx] = { ...u[idx], description: { ...u[idx].description, en: e.target.value } }; setRegistrationConfig({ ...registrationConfig, serviceModes: u }); }} className="sk-control" placeholder="e.g., I have a clinic" />
                          </div>
                          <div className="sk-svc-field"><label>Description (AR)</label>
                            <input type="text" value={mode.description?.ar || ''} onChange={e => { const u = [...registrationConfig.serviceModes]; u[idx] = { ...u[idx], description: { ...u[idx].description, ar: e.target.value } }; setRegistrationConfig({ ...registrationConfig, serviceModes: u }); }} className="sk-control" placeholder="e.g., لدي عيادة" dir="rtl" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {registrationConfig.serviceModes.some(m => m.key === 'hasStore' && m.enabled) && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#566071', marginBottom: 8 }}>Store Address Label</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div className="sk-svc-field"><label>EN</label>
                          <input type="text" value={registrationConfig.storeAddressLabel?.en || ''} onChange={e => setRegistrationConfig({ ...registrationConfig, storeAddressLabel: { ...registrationConfig.storeAddressLabel, en: e.target.value } })} className="sk-control" placeholder="e.g., Clinic Address" />
                        </div>
                        <div className="sk-svc-field"><label>AR</label>
                          <input type="text" value={registrationConfig.storeAddressLabel?.ar || ''} onChange={e => setRegistrationConfig({ ...registrationConfig, storeAddressLabel: { ...registrationConfig.storeAddressLabel, ar: e.target.value } })} className="sk-control" placeholder="e.g., عنوان العيادة" dir="rtl" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          )}

          {/* Custom Registration Fields */}
          <div className="sk-svc-card">
            <div className="sk-svc-card-header">
              <div><h3>Custom Registration Fields <span className="sk-svc-badge">{registrationFields.length}</span></h3></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select className="sk-control" style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, borderColor: '#7c3aed', minWidth: 180 }} value={registrationTemplateId} onChange={e => { const v = e.target.value; setRegistrationTemplateId(v); if (v) applyTemplate(v, 'registration'); }}>
                  <option value="">— No template (custom) —</option>
                  {flowTemplates.filter(t => t.type === 'registration').map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <button type="button" className="sk-svc-add-btn" onClick={addRegistrationField}><PlusIcon /> Add Field</button>
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#7f8999', margin: '0 0 14px' }}>
              Custom questions asked during provider registration for this service (e.g., specialties, years of experience).
            </p>
            {registrationFields.length === 0 ? (
              <p className="sk-svc-empty">No registration fields added yet.</p>
            ) : (
              registrationFields.map((field, index) => (
                <FieldCard
                  key={index} field={field} index={index} formFields={registrationFields}
                  updateFormField={updateRegistrationField} removeFormField={removeRegistrationField} insertFormField={insertRegistrationField}
                />
              ))
            )}
          </div>
        </>
        )}

        {error && <div style={{ color: '#ef4444', padding: '10px 14px', marginBottom: 16, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13 }}>Error: {error}</div>}
        <div className="sk-svc-actions">
          <ActionButton type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</ActionButton>
          <ActionButton variant="secondary" type="button" onClick={() => window.location.hash = '#/defaultservices'}>Cancel</ActionButton>
        </div>
      </form>
    </main>
  );
};


// ── Service icon mapping ─────────────────────────────────────────
const SERVICE_ICONS = {
  'Home Cleaning': 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z M9 22V12h6v10',
  'AC Repair': 'M12 2v4M4.93 4.93l2.83 2.83M2 12h4M4.93 19.07l2.83-2.83M12 18v4M19.07 19.07l-2.83-2.83M22 12h-4M19.07 4.93l-2.83 2.83 M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  'Plumbing': 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1Z M4 22v-7',
  'Personal Training': 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0',
  'Math Tutoring': 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
  'Photography': 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  'Car Detailing': 'M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M5 17H3v-6l2-5h9l4 5h3v6h-2',
  'Pet Grooming': 'M12 2a4 4 0 0 0-4 4v2a4 4 0 0 0 8 0V6a4 4 0 0 0-4-4Z M6 12a2 2 0 1 0 0 4 M18 12a2 2 0 1 0 0 4 M12 16v6',
  'Electrical Work': 'M13 2 3 14h7l-6 8',
  'Moving Help': 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  'Hair Styling': 'M6 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2 M6 10h12',
  'Catering': 'M18 8h1a4 4 0 0 1 0 8h-1 M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z M6 1v3 M10 1v3 M14 1v3',
};
const GENERIC_ICON = 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z M12 6v6l4 2';

const SvcIcon = ({ name, className }) => {
  const d = SERVICE_ICONS[name] || GENERIC_ICON;
  return (
    <span className={`sk-ds-icon ${className || ''}`}>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
};

const CATEGORY_LABELS = {
  home_maintenance: 'Home & Maintenance',
  cleaning_domestic: 'Cleaning & Domestic',
  automotive_transport: 'Automotive & Transport',
  delivery_logistics: 'Delivery & Logistics',
  digital_tech: 'Digital & Tech',
  education_training: 'Education & Training',
  health_wellness: 'Health & Wellness',
  food_events: 'Food & Events',
  art_media: 'Art & Media',
  business_professional: 'Business & Professional',
};

export const DefaultServicesList = props => {
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [mutationLoading, setMutationLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('published');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [modalService, setModalService] = React.useState(null);
  const [tagInput, setTagInput] = React.useState('');
  const [selectedTarget, setSelectedTarget] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    httpClient('https://skilzapp.net/api/defaultservices/all')
      .then(({ json }) => {
        const docs = Array.isArray(json) ? json : (json && Array.isArray(json.docs) ? json.docs : []);
        setServices(docs);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const published = React.useMemo(() => services.filter(s => s.status === 'PUBLISHED'), [services]);
  const drafts = React.useMemo(() => services.filter(s => s.status === 'DRAFT'), [services]);
  const statusFiltered = activeTab === 'published' ? published : drafts;

  // Derive categories with counts from the status-filtered list
  const categoryCounts = React.useMemo(() => {
    const counts = {};
    statusFiltered.forEach(s => {
      const cat = s.category || 'uncategorized';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [statusFiltered]);

  // Apply category + search filters
  const visible = React.useMemo(() => {
    let filtered = statusFiltered;
    if (activeCategory !== 'all') {
      filtered = filtered.filter(s => (s.category || 'uncategorized') === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (Array.isArray(s.tags) && s.tags.some(t => t.toLowerCase().includes(q)))
      );
    }
    return filtered;
  }, [statusFiltered, activeCategory, searchQuery]);

  const handleAccept = async (service) => {
    setMutationLoading(true);
    try {
      await httpClient(`https://skilzapp.net/api/defaultservices/${service._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'PUBLISHED' }),
      });
      await load();
    } catch (err) {
      window.alert('Could not accept service.');
    } finally {
      setMutationLoading(false);
    }
  };

  const handleAddAsTag = () => {
    if (!tagInput || !selectedTarget) return;
    setMutationLoading(true);
    const target = services.find(s => s._id === selectedTarget);
    httpClient(`https://skilzapp.net/api/defaultservices/${selectedTarget}`, {
      method: 'PATCH',
      body: JSON.stringify({ tags: [...(target?.tags || []), tagInput] }),
    }).then(() =>
      httpClient(`https://skilzapp.net/api/defaultservices/${modalService._id}`, { method: 'DELETE' })
    ).then(() => {
      setModalService(null);
      setTagInput('');
      setSelectedTarget(null);
      setMutationLoading(false);
      load();
    }).catch(() => {
      setMutationLoading(false);
      window.alert('Could not add tag.');
    });
  };

  return (
    <div className="sk-ds" id="main-content">
      <section className="sk-ds__header">
        <div>
          <div className="sk-ds__eyebrow">Catalogue</div>
          <h1>Default Services</h1>
          <p>Manage published services and review community submissions.</p>
        </div>
        <a className="sk-ds__create-btn" href="#/defaultservices/create">+ New Service</a>
      </section>

      <nav className="sk-ds__tabs" role="tablist" aria-label="Service status">
        <button
          role="tab"
          type="button"
          aria-selected={activeTab === 'published'}
          className={`sk-ds__tab ${activeTab === 'published' ? 'active' : ''} tone-green`}
          onClick={() => setActiveTab('published')}
        >
          <span className="sk-ds__tab-label">Published</span>
          <span className="sk-ds__tab-count">{published.length}</span>
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={activeTab === 'drafts'}
          className={`sk-ds__tab ${activeTab === 'drafts' ? 'active' : ''} tone-orange`}
          onClick={() => setActiveTab('drafts')}
        >
          <span className="sk-ds__tab-label">Draft Submissions</span>
          <span className="sk-ds__tab-count">{drafts.length}</span>
        </button>
      </nav>

      {/* Search bar */}
      <div style={{ margin: '16px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by name or tag…"
          className="sk-control"
          style={{ flex: 1, maxWidth: 400, borderRadius: 8, borderColor: '#7c3aed', fontSize: 13 }}
        />
        {searchQuery && (
          <button type="button" onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7f8999', fontSize: 13 }}>✕ Clear</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#7f8999' }}>{visible.length} service{visible.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Category filter pills */}
      <div style={{ margin: '12px 0 16px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button
          type="button"
          onClick={() => setActiveCategory('all')}
          style={{
            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: activeCategory === 'all' ? '2px solid #7c3aed' : '1px solid var(--sk-border)',
            background: activeCategory === 'all' ? '#7c3aed' : 'var(--sk-surface)',
            color: activeCategory === 'all' ? '#fff' : '#566071',
          }}
        >
          All ({statusFiltered.length})
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
          const count = categoryCounts[key] || 0;
          if (count === 0) return null;
          const isActive = activeCategory === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveCategory(isActive ? 'all' : key)}
              style={{
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: isActive ? '2px solid #7c3aed' : '1px solid var(--sk-border)',
                background: isActive ? '#7c3aed' : 'var(--sk-surface)',
                color: isActive ? '#fff' : '#566071',
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="sk-ds__loading" role="status">
          <span className="MuiCircularProgress-root" style={{ display: 'inline-block', width: 32, height: 32, animation: 'spin 1s linear infinite', border: '3px solid var(--sk-border)', borderTopColor: 'var(--sk-green)', borderRadius: '50%' }} />
          <span>Loading services…</span>
        </div>
      )}

      {error && !loading && (
        <div className="sk-ds__error" role="alert">
          <p>Services could not be loaded.</p>
          <button className="sk-ds__retry-btn" type="button" onClick={load}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <section className="sk-ds__grid" role="tabpanel">
          {visible.length === 0 && (
            <p className="sk-ds__empty">{activeTab === 'published' ? 'No published services yet' : 'No draft submissions'}</p>
          )}
          {visible.map(service => (
            <Card key={service._id} className="sk-ds__card">
              <CardContent className="sk-ds__card-body">
                <div className="sk-ds__card-top">
                  {service.featuredImage ? (
                    <img
                      className="sk-ds__card-img"
                      src={`https://skilzcdn.nyc3.cdn.digitaloceanspaces.com/${service.featuredImage}`}
                      alt={service.name}
                    />
                  ) : (
                    <SvcIcon name={service.name} className={activeTab === 'published' ? 'green' : 'orange'} />
                  )}
                  <div className="sk-ds__card-info">
                    <strong>{service.name}</strong>
                    {service.category && (
                      <span className="sk-ds__card-category">{CATEGORY_LABELS[service.category] || service.category}</span>
                    )}
                  </div>
                  <span className="sk-ds__card-searches">{service.totalSearches || 0} searches</span>
                </div>

                <p className="sk-ds__card-tags">
                  {Array.isArray(service.tags) ? service.tags.join(', ') : ''}
                </p>

                <div className="sk-ds__card-actions">
                  {activeTab === 'published' ? (
                    <>
                      <a className="sk-ds__btn view" href={`#/defaultservices/${service._id}/show`}>View</a>
                      <a className="sk-ds__btn edit" href={`#/defaultservices/${service._id}`}>Edit</a>
                    </>
                  ) : (
                    <>
                      <button
                        className="sk-ds__btn accept"
                        type="button"
                        disabled={mutationLoading}
                        onClick={() => handleAccept(service)}
                      >
                        Accept
                      </button>
                      <button
                        className="sk-ds__btn add-tag"
                        type="button"
                        disabled={mutationLoading}
                        onClick={() => {
                          setModalService(service);
                          setTagInput(service.name);
                          setSelectedTarget(null);
                        }}
                      >
                        Add as Tag
                      </button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {/* Add-as-Tag modal */}
      <Modal open={modalService !== null} onClose={() => setModalService(null)}>
        <div className="sk-ds__modal">
          {modalService && (
            <>
              <h3 className="sk-ds__modal-title">Add as Tag</h3>
              <p className="sk-ds__modal-desc">
                Add <strong>{modalService.name}</strong> as a search tag inside an existing published service, then remove the draft.
              </p>
              <label className="sk-ds__modal-label">
                Tag name
                <input
                  type="text"
                  className="sk-ds__modal-input"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                />
              </label>
              <label className="sk-ds__modal-label">
                Add to service
                <Select
                  value={selectedTarget || ''}
                  onChange={e => setSelectedTarget(e.target.value)}
                  displayEmpty
                  fullWidth
                  className="sk-ds__modal-select"
                >
                  <MenuItem value="" disabled>Choose a published service…</MenuItem>
                  {published.map(s => (
                    <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </label>
              {selectedTarget && (
                <div className="sk-ds__modal-preview">
                  <span className="sk-ds__modal-preview-label">Updated tags:</span>
                  <p>{tagInput}, {services.find(s => s._id === selectedTarget)?.tags?.join(', ')}</p>
                </div>
              )}
              <div className="sk-ds__modal-actions">
                <button className="sk-ds__btn view" type="button" onClick={() => setModalService(null)}>Cancel</button>
                <button
                  className="sk-ds__btn accept"
                  type="button"
                  disabled={!tagInput || !selectedTarget || mutationLoading}
                  onClick={handleAddAsTag}
                >
                  {mutationLoading ? 'Saving…' : 'Confirm'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};


export const DefaultServicesShow = (props) => {
  const [activeFlowTab, setActiveFlowTab] = React.useState('request');
  const parseFieldType = (ft) => {
    if (ft.startsWith('SELECT:')) return { type: 'SELECT', options: ft.replace('SELECT:', '').split(',').map(o => o.trim()).filter(Boolean) };
    if (ft.startsWith('MULTISELECT:')) return { type: 'MULTISELECT', options: ft.replace('MULTISELECT:', '').split(',').map(o => o.trim()).filter(Boolean) };
    return { type: ft, options: [] };
  };

  return (
    <Show {...props} className="sk-ra-page sk-svc-show-wrap">
      <FunctionField render={record => {
        if (!record) return null;
        const catLabel = CATEGORY_LABELS[record.category] || record.category || '—';
        const tags = Array.isArray(record.tags) ? record.tags : [];
        const fields = record.formFields || [];
        const regFields = record.registrationFields || [];
        return (
          <div className="sk-page sk-svc-form">
            <PageHeader eyebrow="Catalogue" title="View Default Service" />

            {/* Hero */}
            <div className="sk-svc-surface">
              <div className="sk-svc-show-hero">
                {record.featuredImage ? (
                  <img src={`https://skilzcdn.nyc3.cdn.digitaloceanspaces.com/${record.featuredImage}`} alt={record.name} className="sk-svc-show-hero-img" />
                ) : (
                  <Avatar alt={record.name} size="lg" style={{ width: 80, height: 80 }} />
                )}
                <div className="sk-svc-show-hero-meta">
                  <h2 className="sk-svc-show-hero-name">{record.name}</h2>
                  <span className="sk-svc-show-hero-cat">{catLabel}</span>
                </div>
                <StatusPill status={record.status}>{record.status}</StatusPill>
              </div>
            </div>

            {/* Details */}
            <div className="sk-svc-surface">
              <div className="sk-svc-section-header"><h3>Service Details</h3></div>
              <div className="sk-svc-show-grid">
                <div className="sk-svc-show-detail">
                  <span className="sk-svc-show-label">ID</span>
                  <span className="sk-svc-show-value" style={{ fontFamily: 'monospace', fontSize: 12, color: '#7f8999' }}>{record.id}</span>
                </div>
                <div className="sk-svc-show-detail">
                  <span className="sk-svc-show-label">Category</span>
                  <span className="sk-svc-show-value">{catLabel}</span>
                </div>
                <div className="sk-svc-show-detail">
                  <span className="sk-svc-show-label">Status</span>
                  <span className="sk-svc-show-value"><StatusPill status={record.status}>{record.status}</StatusPill></span>
                </div>
                <div className="sk-svc-show-detail">
                  <span className="sk-svc-show-label">Total Searches</span>
                  <span className="sk-svc-show-value">{record.totalSearches || 0}</span>
                </div>
                <div className="sk-svc-show-detail wide">
                  <span className="sk-svc-show-label">Description</span>
                  <span className="sk-svc-show-value">{record.defaultDesc}</span>
                </div>
                {tags.length > 0 && (
                  <div className="sk-svc-show-detail wide">
                    <span className="sk-svc-show-label">Tags</span>
                    <div className="sk-svc-option-chips" style={{ marginTop: 4 }}>
                      {tags.map((t, i) => <span key={i} className="sk-svc-chip">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Flow Tabs */}
            <nav className="sk-svc-tab-nav" style={{ marginBottom: 0 }}>
              <button type="button" className={`sk-svc-tab ${activeFlowTab === 'request' ? 'active' : ''}`} onClick={() => setActiveFlowTab('request')}>
                Send Request Flow <span className="sk-svc-badge">{fields.length}</span>
              </button>
              <button type="button" className={`sk-svc-tab ${activeFlowTab === 'registration' ? 'active' : ''}`} onClick={() => setActiveFlowTab('registration')}>
                Registration Flow <span className="sk-svc-badge">{regFields.length}</span>
              </button>
            </nav>

            {activeFlowTab === 'request' && (
              <div className="sk-svc-card">
                <div className="sk-svc-card-header">
                  <h3>Send Request Fields <span className="sk-svc-badge">{fields.length}</span></h3>
                </div>
                {fields.length === 0 ? (
                  <p className="sk-svc-empty">No send request fields configured.</p>
                ) : (
                  fields.map((field, index) => {
                    const parsed = parseFieldType(field.fieldType);
                    const hasShowIf = field.showIf && field.showIf.field && field.showIf.containsAny && field.showIf.containsAny.length > 0;
                    return (
                      <div key={index} className="sk-svc-field-card">
                        <div className="sk-svc-field-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="sk-svc-field-num">{index + 1}</span>
                            <span className="sk-svc-field-name">{field.fieldName}</span>
                          </div>
                          <span className="sk-svc-field-type">{parsed.type}</span>
                        </div>
                        <div className="sk-svc-field-meta">
                          {field.isRequired
                            ? <span className="sk-svc-required">Required</span>
                            : <span className="sk-svc-optional">Optional</span>
                          }
                          {field.description && <span className="sk-svc-field-desc">📝 {field.description}</span>}
                        </div>
                        {parsed.options.length > 0 && (
                          <div className="sk-svc-chips" style={{ marginTop: 8 }}>
                            {parsed.options.map((opt, oi) => <span key={oi} className="sk-svc-chip">{opt}</span>)}
                          </div>
                        )}
                        {hasShowIf && (
                          <div className="sk-svc-showif">
                            <span className="sk-svc-showif-label">Conditional</span>
                            <span>Shown when <strong>"{field.showIf.field}"</strong> contains</span>
                            <div className="sk-svc-showif-values">
                              {field.showIf.containsAny.map((v, vi) => <span key={vi} className="sk-svc-chip">{v}</span>)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {activeFlowTab === 'registration' && (
              <div className="sk-svc-card">
                <div className="sk-svc-card-header">
                  <h3>Registration Fields <span className="sk-svc-badge">{regFields.length}</span></h3>
                </div>
                {regFields.length === 0 ? (
                  <p className="sk-svc-empty">No registration fields configured. Edit this service to add fields or apply a template.</p>
                ) : (
                  regFields.map((field, index) => {
                    const parsed = parseFieldType(field.fieldType);
                    const hasShowIf = field.showIf && field.showIf.field && field.showIf.containsAny && field.showIf.containsAny.length > 0;
                    return (
                      <div key={index} className="sk-svc-field-card">
                        <div className="sk-svc-field-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="sk-svc-field-num">{index + 1}</span>
                            <span className="sk-svc-field-name">{field.fieldName}</span>
                          </div>
                          <span className="sk-svc-field-type">{parsed.type}</span>
                        </div>
                        <div className="sk-svc-field-meta">
                          {field.isRequired
                            ? <span className="sk-svc-required">Required</span>
                            : <span className="sk-svc-optional">Optional</span>
                          }
                          {field.description && <span className="sk-svc-field-desc">📝 {field.description}</span>}
                        </div>
                        {parsed.options.length > 0 && (
                          <div className="sk-svc-chips" style={{ marginTop: 8 }}>
                            {parsed.options.map((opt, oi) => <span key={oi} className="sk-svc-chip">{opt}</span>)}
                          </div>
                        )}
                        {hasShowIf && (
                          <div className="sk-svc-showif">
                            <span className="sk-svc-showif-label">Conditional</span>
                            <span>Shown when <strong>"{field.showIf.field}"</strong> contains</span>
                            <div className="sk-svc-showif-values">
                              {field.showIf.containsAny.map((v, vi) => <span key={vi} className="sk-svc-chip">{v}</span>)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Searched By */}
            <Surface title="Searched By">
              <Notify users={record.searchedBy ? record.searchedBy : []} />
            </Surface>

            <div className="sk-svc-actions">
              <ActionButton type="button" onClick={() => window.location.hash = `#/defaultservices/${record.id}`}>Edit</ActionButton>
              <DeleteButton />
            </div>
          </div>
        );
      }} />
    </Show>
  );
};

const Notify = ({ users }) => {
  // group by user ID
  const grouped = users?.reduce((acc, user) => {
    acc[user._id] = acc[user._id] || [];
    acc[user._id].push(user);
    return acc;
  }, {});
  const [message, setMessage] = React.useState('');
  const [countryFilter, setCountryFilter] = React.useState('');
  const [cityFilter, setCityFilter] = React.useState('');

  // Build unique countries & cities (cities filtered by selected country)
  const uniqueCountries = React.useMemo(() => {
    return Array.from(new Set((users || []).map(u => u.country).filter(Boolean))).sort();
  }, [users]);

  const uniqueCities = React.useMemo(() => {
    return Array.from(new Set((users || [])
      .filter(u => !countryFilter || u.country === countryFilter)
      .map(u => u.city)
      .filter(Boolean))).sort();
  }, [users, countryFilter]);

  // Filter grouped entries according to selected filters
  const filteredEntries = React.useMemo(() => {
    return Object.entries(grouped || {}).filter(([_, arr]) => {
      const sample = arr[0];
      if (countryFilter && sample.country !== countryFilter) return false;
      if (cityFilter && sample.city !== cityFilter) return false;
      return true;
    });
  }, [grouped, countryFilter, cityFilter]);

  // User IDs to notify (only those in the filtered list)
  const userIds = React.useMemo(() => filteredEntries.map(([userId]) => userId), [filteredEntries]);

  const handleNotify = () => {
    if (userIds.length === 0) {
      alert('No users to notify');
      return;
    }
    if (!message) {
      alert('Please enter a message');
      return;
    }
    const url = `https://skilzapp.net/api/notifications/send`
    httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ message, sendTo: userIds }),
    }).then(({ json }) => {
      setMessage('')
      window.alert('Push Message Sent!')
    })
  };

  return (
    <div className="sk-notify-panel">
      <div className="sk-notify-filters">
        <Select
          value={countryFilter}
          onChange={(e) => { setCountryFilter(e.target.value); setCityFilter(''); }}
          displayEmpty
          className="sk-control"
          style={{ minWidth: 160 }}
          size='small'
        >
          <MenuItem value=''><em>All Countries</em></MenuItem>
          {uniqueCountries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
        <Select
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          displayEmpty
          className="sk-control"
          style={{ minWidth: 160 }}
          size='small'
        >
          <MenuItem value=''><em>All Cities</em></MenuItem>
          {uniqueCities.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
        <span className="sk-muted" style={{ marginLeft: 'auto' }}>Showing {filteredEntries.length} user(s)</span>
      </div>
      <div className="sk-notify-users">
        {filteredEntries.map(([userId, users]) => {
          const sample = users[0];
          return (
            <div key={userId} className="sk-row" style={{ padding: '6px 0' }}>
              <div className="sk-row-text">
                <a href={`/#/users/${sample._id}/show`} className="sk-title">{sample.name} — {sample.phone}</a>
                <span className="sk-meta sk-meta-dim">
                  {sample.city} — {sample.country}
                  {users.length > 1 && ` (${users.length - 1}× repeat)`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="sk-notify-compose">
        <input className="sk-control" type="text" placeholder="Notification message…" value={message} onChange={(e) => setMessage(e.target.value)} />
        <ActionButton type="button" onClick={handleNotify}>Send Notification</ActionButton>
      </div>
    </div>
  );
};
